import liff from '@line/liff';
import { CFG, assertEnv } from '../config/env';

let initOnce = null;

async function bootLiff() {
  assertEnv();
  if (!initOnce) {
    initOnce = (async () => {
      await liff.init({ liffId: CFG.LIFF_ID });   // MUST await init + ready
      await liff.ready;
    })();
  }
  return initOnce;
}

export async function ensureLoggedIn() {
  await bootLiff();

  // If not logged in, hand off to LINE
  if (!liff.isLoggedIn()) {
    liff.login({ redirectUri: CFG.REDIRECT_URI });
    return false;
  }

  // After login+redirect, tokens should exist
  const accessToken = liff.getAccessToken();
  if (!accessToken) {
    // Sometimes iOS returns empty token on first paint → re-login
    liff.login({ redirectUri: CFG.REDIRECT_URI });
    return false;
  }
  return true;
}

export function getTokens() {
  return {
    accessToken: liff.getAccessToken() || '',
    idToken: liff.getIDToken() || '',
    decoded: liff.getDecodedIDToken() || null,
  };
}

// Fetch profile (client-side via LIFF)
export async function loadProfile() {
  await bootLiff();
  return await liff.getProfile();
}

// Get device info
export function getDeviceInfo() {
  return {
    os: liff.getOS(),
    language: liff.getLanguage(),
    version: liff.getVersion(),
    isInClient: liff.isInClient(),
  };
}

// Verify with backend (OIDC) — send **id_token**
export async function verifyWithServer(endpoint) {
  const { idToken } = getTokens();
  if (!idToken) throw new Error('Missing id_token; please login');
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });
  if (!res.ok) throw new Error(`Server verify failed: ${res.status}`);
  return await res.json();
}

// Call LINE REST API — send **access token**
export async function callLineAPI(url) {
  const { accessToken } = getTokens();
  if (!accessToken) throw new Error('Missing access_token; please login');
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`LINE API failed: ${res.status}`);
  return await res.json();
}

// Clear tokens on logout
export function doLogout() {
  try { 
    liff.logout(); 
  } finally { 
    window.location.replace('/'); 
  }
}

export { liff };